import * as DropdownMenu from '@radix-ui/react-dropdown-menu'
import { useNavigate } from 'react-router-dom'

import { useAuth } from '@/auth/AuthProvider'
import { FxAvatar, FxIcon } from '@/components/fx'
import { cn } from '@/lib/utils'
import { initialsOf } from './FunnelLayout'

/**
 * The account control: the avatar from the artboards, opening the two things
 * that have nowhere else to live in an icon rail — the account's own settings
 * and signing out.
 *
 * It sits where the avatar is drawn (the rail's foot on desktop, the top bar
 * on phone) rather than duplicating itself into the utility row, so there is
 * exactly one avatar per surface. On the rail it needs `FxAppRail`'s
 * `accountSlot`, because the built-in account control is a `<button>` and a
 * menu trigger cannot be nested inside one.
 */
export function AccountMenu({
  side = 'bottom',
  size,
}: {
  /** Where the menu opens: `right` off the rail, `bottom` under the top bar. */
  side?: 'right' | 'bottom'
  size?: number
}) {
  const { user, logout } = useAuth()
  const navigate = useNavigate()
  const initials = initialsOf(user)
  const name = user?.displayName?.trim() || user?.email || 'Account'

  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger asChild>
        <button type="button" aria-label="Account" className="flex-none rounded-full">
          {initials ? (
            <FxAvatar initials={initials} size={size} />
          ) : (
            <span
              className="inline-flex items-center justify-center rounded-full border-[1.5px] border-primary-strong bg-surface text-primary-text"
              style={{ width: size ?? 40, height: size ?? 40 }}
            >
              <FxIcon name="person" size={24} filled />
            </span>
          )}
        </button>
      </DropdownMenu.Trigger>
      <DropdownMenu.Portal>
        <DropdownMenu.Content
          side={side}
          align="end"
          sideOffset={8}
          collisionPadding={8}
          className="z-50 min-w-[240px] rounded-xl bg-surface p-2 text-body text-text shadow-overlay"
        >
          <div className="truncate px-3 py-2 text-small text-text-muted" title={name}>
            {name}
          </div>
          <DropdownMenu.Item asChild>
            <button type="button" className={itemStyles} onClick={() => navigate('/einstellungen')}>
              <FxIcon name="settings" size={22} />
              Einstellungen
            </button>
          </DropdownMenu.Item>
          <DropdownMenu.Item asChild>
            <button
              type="button"
              className={itemStyles}
              onClick={() => {
                // The gateway revokes this one refresh family, not every
                // session — so it is "Abmelden", never "Überall abmelden".
                void logout().then(() => navigate('/login'))
              }}
            >
              <FxIcon name="logout" size={22} />
              Abmelden
            </button>
          </DropdownMenu.Item>
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  )
}

const itemStyles = cn(
  'flex w-full cursor-pointer items-center gap-3 rounded-sm px-3 py-2 text-left outline-none',
  'data-[highlighted]:bg-primary-subtle data-[highlighted]:text-primary-text',
)
